import { useState, useEffect } from 'react';
import type { CollectionKey } from '../../shared/types';

export interface PinnedItem {
  id: string; // unique id for the pin itself
  entityId: number;
  type: CollectionKey;
}

export interface RollResult {
  id: number;
  dice: string;
  total: number;
  timestamp: Date;
  rolls?: { die: number; value: number }[];
  modifier?: number;
}

const DASHBOARD_STORAGE_KEY = 'campaign-dashboard-state';

interface DashboardState {
  pinnedItems: PinnedItem[];
  diceHistory: (Omit<RollResult, 'timestamp'> & { timestamp: string })[]; // Store dates as strings
}

export function usePlayDashboard() {
  const loadState = (): DashboardState | null => {
    try {
      const stored = localStorage.getItem(DASHBOARD_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as DashboardState) : null;
    } catch (e) {
      console.error('Failed to load dashboard state', e);
      return null;
    }
  };

  const [pinnedItems, setPinnedItems] = useState<PinnedItem[]>(() => {
    const stored = loadState();
    return stored?.pinnedItems || [];
  });
  const [diceHistory, setDiceHistory] = useState<RollResult[]>(() => {
    const stored = loadState();
    return stored?.diceHistory
      ? stored.diceHistory.map(h => ({
          ...h,
          timestamp: new Date(h.timestamp),
        }))
      : [];
  });

  // Persist state on change
  useEffect(() => {
    const state: DashboardState = {
      pinnedItems,
      diceHistory: diceHistory.map(h => ({
        ...h,
        timestamp: h.timestamp.toISOString()
      }))
    };
    localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify(state));
  }, [pinnedItems, diceHistory]);

  const pinItem = (type: CollectionKey, entityId: number) => {
    setPinnedItems(prev => {
      // Prevent duplicates
      if (prev.some(p => p.type === type && p.entityId === entityId)) {
        return prev;
      }
      return [...prev, { id: Date.now().toString(), type, entityId }];
    });
  };

  const unpinItem = (pinId: string) => {
    setPinnedItems(prev => prev.filter(p => p.id !== pinId));
  };

  const addDiceRoll = (roll: RollResult) => {
    setDiceHistory(prev => [roll, ...prev].slice(0, 50)); // Keep last 50
  };

  const clearDiceHistory = () => {
    setDiceHistory([]);
  };

  return {
    pinnedItems,
    pinItem,
    unpinItem,
    diceHistory,
    addDiceRoll,
    clearDiceHistory
  };
}
